import React from 'react';
import { Helmet } from 'react-helmet-async';
import { LayoutGrid, Server, Zap } from 'lucide-react';
import { MobileSubMenuHeader } from '@/components/layout';
import { navigationItems } from '@/lib/navigation';

interface ServicesProps {
  isGlobalDarkMode: boolean;
  onMobileSubMenuToggle?: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  isMobile?: boolean;
}

const Services: React.FC<ServicesProps> = ({ isGlobalDarkMode, onMobileSubMenuToggle, isMobile = false }) => {
  const handleMobileMenuToggle = () => {
    const crmMenuItem = navigationItems.find(item => item.id === 'crm');
    if (crmMenuItem && onMobileSubMenuToggle) {
      onMobileSubMenuToggle('crm', crmMenuItem.subItems, crmMenuItem.label);
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Services | AgencyPlus</title>
        <meta name="description" content="Manage your service offerings" />
        <link rel="canonical" href="/services/catalog" />
      </Helmet>

      {isMobile && (
        <MobileSubMenuHeader
          onMenuClick={handleMobileMenuToggle}
          showSearch={false}
          showAdd={true}
          isDarkMode={isGlobalDarkMode}
        />
      )}

      <div className={`w-full px-6 md:px-10 py-6 ${isMobile ? 'pt-20' : ''}`}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2 text-foreground">Services</h1>
            <p className="text-muted-foreground">
              Manage and productize your service offerings
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-24 h-24 rounded-full flex items-center justify-center mb-6 bg-blue-500/15">
            <Zap className="w-12 h-12 text-blue-500" />
          </div>

          <h2 className="text-2xl font-bold mb-4 text-foreground">
            Coming Soon
          </h2>

          <p className="text-center max-w-md mb-8 text-muted-foreground">
            We're building a powerful service management system to help you package, sell, and manage your agency services efficiently.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl">
            <div className="p-6 rounded-lg border bg-card border-border shadow-sm">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-workspace-accent/15">
                <LayoutGrid className="w-6 h-6 text-workspace-accent" />
              </div>
              <h3 className="font-semibold mb-2 text-foreground">
                Service Packages
              </h3>
              <p className="text-sm text-muted-foreground">
                Create and manage standardized service packages
              </p>
            </div>

            <div className="p-6 rounded-lg border bg-card border-border shadow-sm">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-amber-500/15">
                <Server className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="font-semibold mb-2 text-foreground">
                Subscription Billing
              </h3>
              <p className="text-sm text-muted-foreground">
                Automate recurring billing for your services
              </p>
            </div>

            <div className="p-6 rounded-lg border bg-card border-border shadow-sm">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 bg-green-500/15">
                <Zap className="w-6 h-6 text-green-500" />
              </div>
              <h3 className="font-semibold mb-2 text-foreground">
                Rapid Delivery
              </h3>
              <p className="text-sm text-muted-foreground">
                Streamline fulfillment of your service orders
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
